import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Banner,
  Button,
  Flex,
  Header,
  OneColumnLayout,
  RadioGroup,
  Spacer,
} from '@electricjs/arc';
import {
  EntitlementSlugEnum,
  LockStatus,
  OSType,
} from '@electricjs/core_entity-client';

import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { useLockDeviceCompat } from '@/hooks/useLockDeviceCompat';
import { useDeviceMdmActions } from '@/hooks/useDeviceMdmActions';
import { Employee } from '@/types/employees';
import { useGlobalUI } from '../GlobalUIProvider';
import WipeDeviceModal from '../People/EmployeeProfile/WipeDeviceModal';
import DeviceCard from './DeviceCard';
import LockDeviceRadioOption from './DeviceData/LockDeviceRadioOption';
import DeviceRadioTile from './DeviceRadioTile';
import { DeviceDataAction, DeviceRetrieveAction } from './types';
import { Asset, DeviceAsset } from '@/types/assets';
import { getDisplayFullName } from 'common/utils/getFullName';

type DeviceDataProps = {
  asset: Asset;
  employee: Employee;
  onNext: () => void;
  onPrevious: () => void;
};

const DeviceData = ({
  asset,
  employee,
  onNext,
  onPrevious,
}: DeviceDataProps) => {
  const { t } = useTranslation('common');
  const organizationId = useGetOrganizationId();
  const { showErrorToast, openModal, closeModal } = useGlobalUI();
  const { control, setValue } = useFormContext();

  const deviceDataAction = useWatch({ name: 'deviceDataAction' });
  const deviceRetrievalAction = useWatch({ name: 'deviceRetrieveAction' });

  const organizationHasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );

  const [lockDevice, { isLoading: isLockingDevice }] = useLockDeviceCompat();

  const { canLock, canWipe } = useDeviceMdmActions(
    asset.device?.mdmProviderId,
    asset.device?.id
  );

  const isShippingToWarehouse =
    deviceRetrievalAction === DeviceRetrieveAction.Warehouse;

  const showLockOption =
    asset.device &&
    asset.device.osType !== OSType.Windows &&
    organizationHasMdmEntitlement &&
    asset.device.mdmEnrolledAt &&
    canLock !== false;
  const disableLockOption =
    isLockingDevice || isShippingToWarehouse || canLock === undefined;

  const showWipeOption =
    asset.device?.mdmEnrolledAt &&
    (isShippingToWarehouse || organizationHasMdmEntitlement) &&
    canWipe !== false;
  const disableWipeOption = isLockingDevice || canWipe === undefined;
  const skipWipeModal = !(
    organizationHasMdmEntitlement && asset.device?.mdmEnrolledAt
  );

  const disableKeepOption = isLockingDevice || isShippingToWarehouse;

  useEffect(() => {
    if (!organizationHasMdmEntitlement) {
      setValue('deviceDataAction', DeviceDataAction.Keep);
    }
  }, [organizationHasMdmEntitlement, setValue]);

  useEffect(() => {
    if (disableKeepOption && (!showLockOption || disableLockOption)) {
      setValue('deviceDataAction', DeviceDataAction.Remove);
    }

    return () => {
      setValue('deviceDataAction', null);
    };
  }, [disableKeepOption, disableLockOption, setValue, showLockOption]);

  useEffect(() => {
    if (
      (!showWipeOption || disableWipeOption) &&
      (!showLockOption || disableLockOption)
    ) {
      setValue('deviceDataAction', DeviceDataAction.Keep);
    }

    return () => {
      setValue('deviceDataAction', null);
    };
  }, [
    disableLockOption,
    disableWipeOption,
    setValue,
    showLockOption,
    showWipeOption,
  ]);

  const deviceId = asset.device?.id || '';
  const deviceSerialNumber = asset.serialNumber;

  if (!deviceSerialNumber) {
    throw new Error(t('offboardings.deviceData.errors.missingSerialNumber'));
  }
  const handleLockDevice = async (onSuccess: () => void) => {
    try {
      await lockDevice({
        organizationId,
        deviceId: deviceId,
        serialNumber: deviceSerialNumber,
      });
      onSuccess();
    } catch (error) {
      window?.DD_RUM?.addError(`Error sending lock command: ${error}`, {
        location: 'OffboardingLockDevice',
      });
      showErrorToast({
        id: 'lock-device-error-toast',
        message: t('offboardings.deviceData.errors.lockCommandFailed'),
      });
    }
  };

  const handleNext = () => {
    if (deviceDataAction === DeviceDataAction.Keep) {
      onNext();
      return;
    }
    if (deviceDataAction === DeviceDataAction.Lock) {
      handleLockDevice(onNext);
    }

    if (deviceDataAction === DeviceDataAction.Remove) {
      if (skipWipeModal) {
        onNext();
        return;
      }

      openModal(
        <WipeDeviceModal
          deviceAsset={asset as DeviceAsset}
          onCompleteWipe={() => {
            closeModal();
            onNext();
          }}
          onCancel={closeModal}
          employeeName={getDisplayFullName(employee)}
        />
      );
    }
  };

  return (
    <OneColumnLayout>
      <Header title={t('offboardings.deviceData.title')} />
      <DeviceCard asset={asset} />
      <Spacer y="2rem" />
      {isShippingToWarehouse && (
        <Banner
          id="warehoused-devices-data-banner"
          intent="warning"
          isFlat
          title={t('offboardings.deviceData.warehouseBannerTitle')}
          message={t('offboardings.deviceData.warehouseBannerMessage')}
        />
      )}
      <Controller
        name="deviceDataAction"
        control={control}
        render={({ field }) => (
          <RadioGroup
            {...field}
            mt="4.2rem"
            gap="2.2rem"
            selectTilesBorderWidth="2px">
            <>
              {showWipeOption && (
                <DeviceRadioTile
                  checked={deviceDataAction === DeviceDataAction.Remove}
                  value={DeviceDataAction.Remove}
                  optionText={t('offboardings.deviceData.options.wipe')}
                  icon="broom"
                  disabled={disableWipeOption}
                />
              )}
            </>
            <>
              {showLockOption && (
                <LockDeviceRadioOption
                  lockStatus={asset.device?.lockStatus ?? LockStatus.Unlocked}
                  value={deviceDataAction}
                  disabled={disableLockOption}
                />
              )}
            </>
            <DeviceRadioTile
              value={DeviceDataAction.Keep}
              optionText={t('offboardings.deviceData.options.keep')}
              secondaryText={t('offboardings.deviceData.options.keepSecondary')}
              icon="laptop"
              checked={deviceDataAction === DeviceDataAction.Keep}
              disabled={disableKeepOption}
            />
          </RadioGroup>
        )}
      />
      <Flex mt="9rem" hAlignContent="between" gap="3.4rem">
        <Button
          id="devices-data-previous-button"
          variant="outline"
          disabled={isLockingDevice}
          onClick={onPrevious}>
          {t('offboardings.deviceData.previousButton')}
        </Button>
        <Button
          id="devices-data-next-button"
          disabled={!deviceDataAction}
          loading={isLockingDevice}
          onClick={handleNext}>
          {t('offboardings.deviceData.nextButton')}
        </Button>
      </Flex>
    </OneColumnLayout>
  );
};

export default DeviceData;
